import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import DynamicModal from "src/components/modal/DynamicModal";
import { CommonAlert } from "src/components/alert/CommonAlert";
import { confirmDiscard } from "./helpers";

/**
 * The shared form dialog, with one change: once anything has been typed,
 * picked or dropped into it, closing it (Escape, a click outside, the close
 * button) asks "Discard this ...?" instead of silently throwing the input
 * away. Same props as DynamicModal, plus `discardTitle`.
 *
 * The form's state lives inside DynamicModal, so "has anything been typed"
 * is read from the input and change events inside the dialog and from its
 * onChange callback (dropdowns, pickers).
 */
const GuardedModal = ({ show, setShow, onChange, discardTitle, ...props }) => {
  const alert = CommonAlert();
  const dirty = useRef(false);

  useEffect(() => {
    dirty.current = false;
    if (!show) return undefined;
    const mark = (event) => {
      if (event.target?.closest?.("[data-dynamic-modal]")) dirty.current = true;
    };
    document.addEventListener("input", mark, true);
    document.addEventListener("change", mark, true);
    return () => {
      document.removeEventListener("input", mark, true);
      document.removeEventListener("change", mark, true);
    };
  }, [show]);

  const guardedSetShow = (open) => {
    if (open || !dirty.current) return setShow(open);
    return confirmDiscard(alert, { title: discardTitle, onDiscard: () => setShow(false) });
  };

  return (
    <DynamicModal
      {...props}
      show={show}
      setShow={guardedSetShow}
      onChange={(args) => {
        dirty.current = true;
        onChange?.(args);
      }}
    />
  );
};

GuardedModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  discardTitle: PropTypes.string.isRequired,
};

export default GuardedModal;
